export async function extractFramesFromVideo(
  videoBlob: Blob,
  frameIntervalSeconds: number = 3,
  knownDuration?: number
): Promise<Blob[]> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    const timeout = setTimeout(() => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Frame extraction timeout - video took too long to process'));
    }, 30000);

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    const frames: Blob[] = [];
    let currentFrameIndex = 0;

    video.onloadedmetadata = () => {
      let duration = video.duration;

      if (!duration || !isFinite(duration) || duration <= 0) {
        if (knownDuration && knownDuration > 0) {
          console.log('[Frame Extractor] Video duration invalid, using known duration:', knownDuration);
          duration = knownDuration;
        } else {
          clearTimeout(timeout);
          URL.revokeObjectURL(video.src);
          reject(new Error(`Invalid video duration: ${duration}, no fallback available`));
          return;
        }
      }

      console.log('[Frame Extractor] Video duration:', duration, 'seconds');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const frameCount = Math.max(1, Math.ceil(duration / frameIntervalSeconds));
      const interval = frameIntervalSeconds;

      console.log('[Frame Extractor] Extracting', frameCount, 'frames at', interval.toFixed(2), 'second intervals');

      const extractFrame = async (index: number) => {
        if (index >= frameCount) {
          clearTimeout(timeout);
          URL.revokeObjectURL(video.src);
          console.log('[Frame Extractor] Successfully extracted', frames.length, 'frames');
          resolve(frames);
          return;
        }

        const time = Math.min(index * interval, duration - 0.1);

        if (!isFinite(time) || time < 0) {
          clearTimeout(timeout);
          URL.revokeObjectURL(video.src);
          reject(new Error(`Invalid time value: ${time}`));
          return;
        }

        video.currentTime = time;
      };

      video.onseeked = async () => {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          if (blob) {
            frames.push(blob);
          }
          currentFrameIndex++;
          extractFrame(currentFrameIndex);
        }, 'image/jpeg', 0.8);
      };

      video.onerror = (e) => {
        clearTimeout(timeout);
        URL.revokeObjectURL(video.src);
        reject(new Error('Error loading video'));
      };

      extractFrame(0);
    };

    video.onerror = (e) => {
      clearTimeout(timeout);
      reject(new Error('Error creating video element'));
    };

    video.src = URL.createObjectURL(videoBlob);
  });
}
